import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Card,
  Typography,
  App,
  Button,
  Tooltip,
  Row,
  Col,
  Divider
} from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ReloadOutlined, LeftOutlined, CheckCircleTwoTone, CloseCircleTwoTone, ClockCircleTwoTone, } from '@ant-design/icons';
import { resolveMedia } from '../../utils/media';
import { getCompetitionById, listPreliminaryVideos } from '../../services/competitions';
import useAuth from '../../hooks/useAuth';

import '../../styles/evaluation.css';
import dayjs from 'dayjs';
import { Space } from 'antd';
import usePermissions from '../../hooks/usePermissions';

const { Title, Text } = Typography;
const { useApp } = App;

const STATUS = {
  EVALUATED: 'EVALUATED',
  ACCEPTED: 'ACCEPTED',
  REJECTED: 'REJECTED',
};

const statusIcons = {
  EVALUATED: (
    <ClockCircleTwoTone
      twoToneColor="#52c41a"
      style={{ fontSize: 42 }}
    />
  ),
  ACCEPTED: (
    <CheckCircleTwoTone
      twoToneColor="#BA955E"
      style={{ fontSize: 42 }}
    />
  ),
  REJECTED: (
    <CloseCircleTwoTone
      twoToneColor="#ff4d4f"
      style={{ fontSize: 42 }}
    />
  ),
};


const PreliminaryEvaluationList = () => {
  
  const { message } = useApp();
  const { user } = useAuth();
  const { id } = useParams(); // competitionId
  const navigate = useNavigate();
  const { can } = usePermissions();

  const nickname = `${user?.firstName} ${user?.lastName} ${user?.secondLastName || ''}` || user?.nickname || user?.email;

  const [competition, setCompetition] = useState(null);

  const [queryKey, setQueryKey] = useState(0);

  const createInitialState = () => ({
    items: [],
    page: 0,
    hasMore: true,
    total: 0,
  });

  const [pending, setPending] = useState(createInitialState());
  const [evaluated, setEvaluated] = useState(createInitialState());
  const [rejected, setRejected] = useState(createInitialState());

  const pendingRef = useRef(pending);
  const evaluatedRef = useRef(evaluated);
  const rejectedRef = useRef(rejected);

  const loadingRef = useRef({
    EVALUATED: false,
    ACCEPTED: false,
    REJECTED: false,
  });

  useEffect(() => { pendingRef.current = pending; }, [pending]);
  useEffect(() => { evaluatedRef.current = evaluated; }, [evaluated]);
  useEffect(() => { rejectedRef.current = rejected; }, [rejected]);

  const resetAndReload = () => {
    setPending(createInitialState());
    setEvaluated(createInitialState());
    setRejected(createInitialState());
  };

  const fetchData = useCallback(async (
    page,
    pageSize,
    search,
    status,
    userId
  ) => {
    const params = {
      competitionId: id,
      search: search || undefined,
      page,
      limit: pageSize,
      ...(status && { status }),
      ...(userId && { userId }),
    };

    return await listPreliminaryVideos(id,params);
  }, [id]);

  const loadMore = useCallback(async (type) => {
    if (loadingRef.current[type]) return;

    const stateMap = {
      EVALUATED: [pendingRef, setPending],
      ACCEPTED: [evaluatedRef, setEvaluated],
      REJECTED: [rejectedRef, setRejected],
    };

    const [stateRef, setState] = stateMap[type];
    const state = stateRef.current;

    if (!state.hasMore) return;

    loadingRef.current[type] = true;

    try {
      const { items, total } = await fetchData(
        state.page + 1,
        10,
        undefined,
        type,
        ['Superadmin', 'admin'].includes(user?.role) ? undefined : user?.id
      );

      setState(prev => {
        const existingIds = new Set(prev.items.map(v => v.id));
        const filtered = items.filter(v => !existingIds.has(v.id));
        const nextItems = [...prev.items, ...filtered];

        return {
          ...prev,
          items: nextItems,
          page: prev.page + 1,
          hasMore: nextItems.length < total,
          total,
        };
      });
    } finally {
      loadingRef.current[type] = false;
    }
  }, [
    fetchData,
    user,
  ]);

  useEffect(() => {
    loadMore(STATUS.EVALUATED);
    loadMore(STATUS.ACCEPTED);
    loadMore(STATUS.REJECTED);
  }, [queryKey, loadMore]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [
          competitionData,
        ] = await Promise.all([
          getCompetitionById(id),
        ]);

        setCompetition(competitionData);
      } catch (error) {
        console.error(error);
        message.error('Error al cargar información');
      }
    };

    loadInitialData();
  }, [id, message]);

  const handleSearch = () => {
    resetAndReload();
    setQueryKey(prev => prev + 1);
  };

  const handleBack = () => {
    navigate('/evaluations/preliminary/all');
  };

  const VideoGrid = ({ videos, status }) => (
    <Row gutter={[16, 16]}>
      {videos.map(video => {
        const content = (
          <div className={`video-item ${status}`}>
            <div className="status-icon-overlay">
                {statusIcons[status]}
            </div>
            <img src={resolveMedia(video.urlThumbail)} alt={video.folio} />
            <div className="video-info">
              <Text>{video.region?.description}</Text>
              <Text type="secondary">{video.folio}</Text>
            </div>
          </div>
        );

        return (
          <Col key={video.id} xs={24} sm={12} md={8} lg={6}>
            {status === STATUS.EVALUATED && evaluated.total < competition?.finalistLimit && can('preliminary.approve') ? (
              <Link to={`/evaluations/preliminary/${video.id}/rate`}>
                {content}
              </Link>
            ) : content}
          </Col>
        );
      })}
    </Row>
  );

  return (
    <div style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button 
                type="text" 
                icon={<LeftOutlined style={{ fontSize: '24px', strokeWidth: 4 }} />} 
                style={{ 
                padding: 0,
                color: '#BA955E',
                fontSize: '24px',
                fontWeight: '700',
                height: 'auto'
                }}
                onClick={handleBack}
            />
            <Space direction="vertical" size={-14} style={{ width: '100%' }}>
                <Title level={3}>Evaluación preliminar</Title>
                <Title level={5}>Semifinalistas</Title>
            </Space>
                <Tooltip title="Recargar">
                    <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    shape="circle"
                    onClick={handleSearch}
                    />
                </Tooltip>
        </div>
      
      <Card>
        <Row gutter={[16, 8]}>
          <Col span={24}><b>Competencia:</b> {competition?.description || '-'} | ({competition?.folio})</Col>
          {/* <Col span={12}>
            <Tooltip title="Recargar">
                <Button
                type="primary"
                icon={<ReloadOutlined />}
                shape="circle"
                onClick={handleSearch}
                />
            </Tooltip>
          </Col> */}
          <Col span={6}><b>Modalidad:</b> {competition?.modality?.description}</Col>
          <Col span={6}><b>Género:</b> {competition?.gender?.description}</Col>
          <Col span={6}><b>Periodicidad:</b> {competition?.period?.description}</Col>
          <Col span={6}>
            <b>Localidad:</b>{' '}
                {competition?.regions?.length
                    ? competition.regions
                        .map(r => r.region?.description)
                        .filter(Boolean)
                        .join(', ')
                    : '—'
                }
            </Col>
          <Col span={6}><b>Fecha:</b> {competition?.endDate ? dayjs(competition?.endDate).format('DD/MM/YYYY') : '-'}</Col>
          <Col span={6}><b>Cantidad:</b> {competition?.finalistLimit} videos</Col>
          <Col span={6}><b>Evaluador:</b> {nickname}</Col>
        </Row>
      </Card>

      <Divider />

      <Card>

        <h2>Videos pendientes por evaluar - <span style={{color: '#74b567'}}>{pending.total} videos</span></h2>
        <InfiniteScroll dataLength={pending.items.length} next={() => loadMore(STATUS.EVALUATED)} hasMore={pending.hasMore}>
            <VideoGrid videos={pending.items} status={STATUS.EVALUATED} />
        </InfiniteScroll>

        <h2>Videos pre evaluados - <span style={{color: '#74b567'}}>{evaluated.total} videos</span></h2>
        <InfiniteScroll dataLength={evaluated.items.length} next={() => loadMore(STATUS.ACCEPTED)} hasMore={evaluated.hasMore}>
            <VideoGrid videos={evaluated.items} status={STATUS.ACCEPTED} />
        </InfiniteScroll>

        <h2>Videos rechazados · <span style={{color: '#74b567'}}>{rejected.total} videos</span></h2>
        <InfiniteScroll dataLength={rejected.items.length} next={() => loadMore(STATUS.REJECTED)} hasMore={rejected.hasMore}>
            <VideoGrid videos={rejected.items} status={STATUS.REJECTED} />
        </InfiniteScroll>
      </Card>
    </div>
  );
};

export default PreliminaryEvaluationList;
